import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Button } from "../Button";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";

type ImpactEmbedModalProps = {
  open: boolean;
  kioskId: string;
  kioskName: string;
  widgetUrl: string;
  onClose: () => void;
};

export const ImpactEmbedModal: React.FC<ImpactEmbedModalProps> = ({
  open,
  kioskId,
  kioskName,
  widgetUrl,
  onClose,
}) => {
  const [embedCopied, setEmbedCopied] = useState(false);

  if (!open) {
    return null;
  }

  const embedSnippet = `<iframe src="${widgetUrl}" title="Tap2Feed live impact" width="100%" height="260" scrolling="no" style="border:0;border-radius:12px;overflow:hidden;display:block"></iframe>`;

  const handleCopy = async (text: string) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setEmbedCopied(true);
      showSuccessToast("Live Impact embed code copied to clipboard");
    } catch {
      showErrorToast("Could not copy. Please copy it manually.");
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/50 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Live Impact Widget Embed
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {kioskName ? `${kioskName} (${kioskId})` : kioskId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Live Preview */}
        <div className="mt-4">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
            Live Preview
          </label>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-800 dark:bg-slate-950">
            <iframe
              src={widgetUrl}
              title="Tap2Feed live impact preview"
              width="100%"
              height="260"
              scrolling="no"
              style={{ border: 0, borderRadius: 12, overflow: "hidden", display: "block" }}
            />
          </div>
        </div>

        {/* Iframe Embed Code */}
        <div className="mt-4">
          <label className="mb-1 block text-sm font-semibold text-gray-800 dark:text-gray-200">
            💻 Website & Blog Iframe Embed
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
            Paste this snippet into your HTML, WordPress, Webflow, or Shopify site to embed the live widget.
          </p>
          <div className="flex flex-col gap-2">
            <textarea
              readOnly
              rows={3}
              value={embedSnippet}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 font-mono text-xs text-gray-800 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-gray-200"
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  void handleCopy(embedSnippet);
                }}
              >
                Copy Embed Code
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
